import SitemapTemplate, {
  SitemapTemplateProps,
} from '@/components/template/SitemapTemplate/SitemapTemplate';
import { getAllStates, getTopCities } from '@/lib/schema/geo/fetch';
import { City } from '@/lib/schema/geo/types';
import { slugify } from '@/lib/utils';
import { BasePageProps } from '@/types/page';
import { GetStaticProps } from 'next';

interface PageProps extends BasePageProps {
  makes: string[];
  topCities: City[];
  stateCodes: string[];
  title: string;
}

const Page: React.FC<PageProps> = ({ makes, topCities, stateCodes, title }) => {
  const templateProps: SitemapTemplateProps = {
    title,
    categories: [
      {
        categoryName: 'Vehicles by Condition',
        links: [
          { displayName: 'New', href: '/new-cars' },
          { displayName: 'Used', href: '/used-cars' },
        ],
      },
      {
        categoryName: 'New Vehicles by Make',
        links: makes.map((make) => ({
          displayName: make,
          href: slugify(`/new-cars/${make}`),
        })),
      },
      {
        categoryName: 'Used Vehicles by Make',
        links: makes.map((make) => ({
          displayName: make,
          href: slugify(`/used-cars/${make}`),
        })),
      },
      {
        categoryName: 'New Vehicles in Top Rated Cities',
        links: topCities.map(({ slug, name }) => {
          return {
            displayName: name,
            href: `/new-cars/${slug}`,
          };
        }),
      },
      {
        categoryName: 'Used Vehicles in Top Rated Cities',
        links: topCities.map(({ slug, name }) => {
          return {
            displayName: name,
            href: `/used-cars/${slug}`,
          };
        }),
      },
      {
        categoryName: 'More Vehicles by State',
        links: stateCodes.map((state) => ({
          displayName: state.toUpperCase(),
          href: `/sitemap/${state}`,
        })),
      },
    ],
  };

  return <SitemapTemplate {...templateProps} />;
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const makesRes = await fetch(
    `${process.env.API_ENDPOINT}/api/inventory/listings/makes_with_inventory`
  );
  const makes: string[] = makesRes.ok ? await makesRes.json() : [];

  const topCities = await getTopCities();
  const stateCodes = (await getAllStates()).map(({ code }) => code);

  const title = 'Sitemap';

  return {
    props: {
      makes,
      topCities,
      stateCodes,
      title,
      metadata: {
        title,
      },
    },
    revalidate: 3600, // Revalidate every hour
  };
};

export default Page;
